import { useEffect, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import './styles/search-box.css';
import CareMapprButton from '../../../../components/Button/CareMapprButton';
import { AdornmentInput } from '../../../../components/Inputs/textField';
import {
  SelectionStatus,
  useFilterSelection,
  usePatientSearch,
} from '../../hooks';
import FilterBadge from '../quickFilters/FilterBadge';
import QuickFilters from '../quickFilters/QuickFilters';
import SavedFilters from '../savedFilters/SavedFilters';
import {
  PatientSearchFilters,
  SearchFilterType,
  type QuickFilter,
  type SavedSearchFilter,
} from '../../types';
import { ExclamationCircle } from 'react-bootstrap-icons';
import { useToast, useTriggerUpdate } from '../../../../../context';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../../store';
import {
  generateFilterOption,
  getCurrentParentFilter,
  validateFiltersCombinations,
} from '../../utils';
import { validateRangeFilter } from '../../validator/main';

interface SearchBoxProps {
  placeholder?: string;
}

export const SearchBox = ({
  placeholder = 'Use Quick Search to filter your patient dashboard',
}: SearchBoxProps) => {
  const { resetHaveMoreData } = useTriggerUpdate();
  const {
    selectedFilters,
    availableFilterOptions,
    isFetching,
    onRemoveSelectedFilter,
    handleFilterSelection,
    inputRef,
    endFlow,
    currentFlow,
    fetchRemoteFilters,
    currentFilter,
    flowStatus,
    continueNext,
  } = useFilterSelection();

  const {
    onUnfocusedSearch,
    onFocusedSearch,
    showSaveSearchButton,
    toggleSavedSearch,
    showSavedSearch,
    onFocusOnSearch,
    onChangeSearchText,
    searchText,
    searchPatients,
    savedSearchFilters,
    onSelectSavedFilter,
    setSearchText,
    onSaveSearchToPatient,
    isSaving,
    quickFiltersToStringArray,
  } = usePatientSearch(selectedFilters, currentFlow, flowStatus);

  const { setToast } = useToast();
  const { userSavedFilters } = useSelector(
    (state: RootState) => state.dashboardSearch,
  );
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const savedSearchDisabled =
    selectedTenancy?.features?.searchScreen?.savedSearchDisabled ?? false;
  const specificPatientLabel =
    useSelector(
      (state: RootState) =>
        state.clinicianProfile.selectedTenancy?.features?.searchFiltersMap
          ?.SpecificPatient?.label,
    ) || PatientSearchFilters.SpecificPatient;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const searchBoxContainerElement = document.querySelector(
        '.search-box-container',
      );
      if (
        searchBoxContainerElement &&
        !searchBoxContainerElement.contains(event.target as Node)
      ) {
        onUnfocusedSearch();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const onSearch = () => {
    if (!validateFiltersCombinations(selectedFilters)) {
      return;
    }
    resetHaveMoreData(true);
    const noFilters = selectedFilters.length === 0;
    if (noFilters && searchText.length > 1) {
      const searchPatientFilters = [specificPatientLabel, searchText].map(
        (item, index) =>
          generateFilterOption(
            item,
            `${PatientSearchFilters.SpecificPatient.trim()}-${index}`,
            {
              filterKey: index === 0 ? 'SpecificPatient' : undefined,
            },
          ),
      );
      searchPatients(searchPatientFilters);
      return;
    }
    if (
      selectedFilters.length > 0 &&
      (selectedFilters[selectedFilters.length - 1]?.filterKey ===
        'SpecificPatient' ||
        currentFlow === SearchFilterType.SEARCH_AND_SINGLE_SELECT)
    ) {
      const searchPatientFilters = [specificPatientLabel, searchText].map(
        (item, index) =>
          generateFilterOption(
            item,
            `${PatientSearchFilters.SpecificPatient.trim()}-${index}`,
            {
              filterKey: index === 0 ? 'SpecificPatient' : undefined,
            },
          ),
      );
      searchPatients(searchPatientFilters);
      return;
    }
    searchPatients(selectedFilters);
  };

  const startAdornments: React.ReactElement[] = useMemo(() => {
    return selectedFilters.map((filter) => (
      <FilterBadge
        className="my-1"
        key={filter.id}
        filter={filter}
        onClick={onRemoveSelectedFilter}
      />
    ));
  }, [selectedFilters]);

  const ageFilterInvalid = useMemo(() => {
    if (flowStatus !== SelectionStatus.AGE) {
      return false;
    }
    const ageFilters = selectedFilters.filter(
      (selected) =>
        selected.filterKey === 'AgeFilter' ||
        selected.id.includes(PatientSearchFilters.AgeFilter),
    );
    const ageInvalid = !validateRangeFilter(
      ageFilters.map((filter) => filter.label),
    );
    return ageInvalid;
  }, [selectedFilters, flowStatus]);

  const searchDisabled = useMemo(() => {
    const noFilters = selectedFilters.length === 0;
    if (noFilters && searchText.length > 1) {
      return false;
    }
    if (
      selectedFilters[selectedFilters.length - 1]?.filterKey ===
        'SpecificPatient' ||
      currentFlow === SearchFilterType.SEARCH_AND_SINGLE_SELECT
    ) {
      return searchText.length < 2;
    }
    const notCompletedFlow =
      currentFlow !== null && currentFlow !== SearchFilterType.NO_SELECTION;
    return noFilters || notCompletedFlow || ageFilterInvalid;
  }, [currentFlow, selectedFilters.length, ageFilterInvalid, searchText]);

  const checkSearchFilterDuplicate = (
    filterText: string,
    userSavedFilters: SavedSearchFilter[],
  ): boolean => {
    const normalize = (text: string) =>
      text
        .split(/\s*,\s*/)
        .map((str) => str.trim().toLowerCase())
        .filter((str) => str.length > 0)
        .sort()
        .join(',');

    const normalizedSearchText = normalize(filterText);

    return userSavedFilters.some(
      (filter) => normalize(filter.text) === normalizedSearchText,
    );
  };

  const onSaveSearch = () => {
    let results: QuickFilter[] = [];
    let stringText = '';

    if (
      selectedFilters.length > 0 &&
      (selectedFilters[selectedFilters.length - 1]?.filterKey ===
        'SpecificPatient' ||
        currentFlow === SearchFilterType.SEARCH_AND_SINGLE_SELECT)
    ) {
      results = [specificPatientLabel, searchText].map((item, index) =>
        generateFilterOption(
          item,
          `${PatientSearchFilters.SpecificPatient.trim()}-${index}`,
          {
            filterKey: index === 0 ? 'SpecificPatient' : undefined,
          },
        ),
      );
      stringText = quickFiltersToStringArray(results).join(', ');
    } else if (selectedFilters.length === 0) {
      results = [specificPatientLabel, searchText].map((item, index) =>
        generateFilterOption(
          item,
          `${PatientSearchFilters.SpecificPatient.trim()}-${index}`,
          {
            filterKey: index === 0 ? 'SpecificPatient' : undefined,
          },
        ),
      );
    } else {
      results = selectedFilters;
      stringText = quickFiltersToStringArray(results).join(', ');
    }
    const isDuplicate = checkSearchFilterDuplicate(
      stringText,
      userSavedFilters,
    );
    if (isDuplicate) {
      setToast(
        <div className="d-flex justify-content-center align-items-center gap-3 text-center">
          This filter already exists <ExclamationCircle color="#E0B36A" />
        </div>,
      );
      return;
    }
    onSaveSearchToPatient(results);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (searchDisabled) return;
      if (e.key === 'Enter') {
        onSearch();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [searchDisabled, onSearch]);

  return (
    <div className="search-box-container">
      <Row className="search-box-field">
        <Col sm={10}>
          <AdornmentInput
            ref={inputRef}
            value={searchText}
            placeholder={placeholder}
            className="search-box-input"
            isFocused={onFocusOnSearch}
            onFocus={onFocusedSearch}
            startAdornments={startAdornments}
            onChange={onChangeSearchText}
          />
        </Col>
        <Col sm={2} className="d-flex gap-1">
          <CareMapprButton
            label="Search"
            onClick={onSearch}
            disabled={searchDisabled}
            extraClassNames="m-0 search-button"
          />
          {!searchDisabled && showSaveSearchButton && (
            <CareMapprButton
              label={isSaving ? 'Saving ...' : 'Save search'}
              onClick={onSaveSearch}
              extraClassNames="m-0 save-search-button"
              variant="link"
              disabled={isSaving}
            />
          )}
        </Col>
      </Row>
      {!savedSearchDisabled && (
        <SavedFilters
          onSelectSavedFilter={onSelectSavedFilter}
          savedSearchFilters={savedSearchFilters}
          showSavedSearch={showSavedSearch}
          toggleSavedSearch={toggleSavedSearch}
          onFocusOnSearch={onFocusOnSearch}
        />
      )}
      {!showSavedSearch && onFocusOnSearch && (
        <QuickFilters
          quickFilters={availableFilterOptions}
          onSelectFilter={(filter) => {
            handleFilterSelection(filter, searchText);
            setSearchText('');
          }}
          isFetching={isFetching}
          currentFilter={currentFilter}
          searchText={searchText}
          selectedParentFilter={getCurrentParentFilter(selectedFilters)}
          fetchRemoteFilters={fetchRemoteFilters}
          endFlow={endFlow}
          mode={flowStatus}
          continueNext={(text) => {
            continueNext(text);
            setSearchText('');
          }}
          selectedFilters={selectedFilters}
        />
      )}
    </div>
  );
};
